// packages/core/src/learning/school-modules/fire-movement.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "FIRE movement". surface: 'school', homeSchool: 'fire-movement'.
// module id 'school-fire-movement' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #1f9e8f) — flag for the presentation-tuning pass.
// tier/pillar: freedom-path lens → tier 'freedom-path', pillar 'freedom-math'.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_FIRE_MOVEMENT_MODULE: LearningModule = {
	id: 'school-fire-movement',
	title: 'FIRE movement',
	description:
		'The lens that treats money as stored time — using savings rate, runway, and invested assets to turn work from a trap into a choice.',
	emoji: '🔥', // PLACEHOLDER — tune in presentation pass
	accentColor: '#1f9e8f', // from SchoolMeta color — tune in presentation pass
	tier: 'freedom-path',
	pillar: 'freedom-math',
	order: 0,
	surface: 'school',
	homeSchool: 'fire-movement',
	lessons: [
		{
			id: 'school-fire-movement-01',
			title: 'FIRE is about buying back time',
			description:
				'This lesson teaches the central FIRE idea: money is not the goal, more control over your time is.',
			pillar: 'freedom-math',
			schoolsOfThought: ['fire-movement'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['time-freedom', 'paycheck-dependence', 'freedom-gap'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['time freedom', 'paycheck dependence', 'freedom gap'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A lot of people hear FIRE and think it means quitting work forever, living on rice, and judging everyone who enjoys life. That is the shallow version. The deeper version is simpler: what would your life feel like if money gave you more control over your time?',
				},
				{
					type: 'paragraph',
					text: 'FIRE sees money as a time machine. Every dollar you keep and grow can reduce how dependent you are on the next paycheck. That does not mean every dollar must be saved. It means the tradeoff becomes visible.',
				},
				{
					type: 'bullet_list',
					items: [
						'More savings can mean more future choice',
						'Lower required spending can mean a smaller freedom target',
						'Invested assets can become a bridge away from paycheck pressure',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking FIRE is only about early retirement. The better question is: how much control are you buying back?',
				},
				{
					type: 'example',
					text: 'Someone earning $70,000 and spending almost all of it may look successful but feel trapped. Someone earning less but saving steadily may have fewer status signals and more future options.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is real. If FIRE becomes too extreme, it can turn life into a waiting room where today never matters. It can also ignore that some people have higher costs, family duties, or unstable income.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your Dashboard and Simulate work. You are not trying to worship a number. You are asking what your current behavior is doing to your future time.',
				},
				{
					type: 'key_takeaway',
					text: 'FIRE is strongest when it turns money into options, not when it turns life into a spreadsheet.',
				},
			],
			audioScript:
				'FIRE gets misunderstood because the loudest version sounds extreme. Quit your job at thirty five. Save almost everything. Never spend on anything fun. That version gets attention, but it is not the only useful lesson. The real lens is this: money can buy back control over your time.\n\nThink about what most people are actually trying to escape. It is not always work itself. Some people like their work. They like building, helping, creating, leading, solving problems, or being around a team. What they hate is having no choice. They hate needing the job even when the job becomes toxic. They hate staying in a city they cannot afford because their paycheck is tied there. They hate saying yes to every extra demand because one missed paycheck would hurt.\n\nFIRE looks at that pressure and says, what if the goal of money is not just more stuff? What if the goal is fewer forced choices?\n\nThat changes the way you see normal decisions. A raise is not only permission to upgrade everything. It can be a chance to widen the gap between income and spending. A bonus is not only a shopping event. It can become runway. A lower rent situation is not just cheaper housing. It can be time bought back from the future.\n\nBut this lens has to stay human. If FIRE turns into obsession, it can become another trap. You can start treating every dinner, trip, hobby, or relationship like an enemy of your future. That is not freedom. That is fear wearing a spreadsheet costume.\n\nThe useful version is more balanced. You ask: what would make me less trapped? Maybe you do not want to retire early. Maybe you want the option to change careers. Maybe you want a year of runway. Maybe you want to work part time later. Maybe you want to say no to a bad boss. Maybe you want to move, start a business, care for family, or recover from burnout without panic.\n\nThat is the heart of FIRE. Money is not the trophy. Time is. Choice is. The ability to make a move before life forces your hand is the point.\n\nIn Nisk, you would test this by looking at your current savings rate and running scenarios. Not because the projection is a promise. It is not. The point is to see direction. If you save more, spend less, earn more, or need less, what changes? How much future pressure comes off your shoulders?\n\nFIRE is not a command to quit working. It is a lens that asks whether your money is quietly buying freedom or quietly buying more dependence.',
			flashcards: [
				{
					id: 'school-fire-movement-01-fc-1',
					role: 'main_idea',
					front: 'What is FIRE really about?',
					back: 'Buying back time, choice, and freedom from paycheck pressure.',
				},
				{
					id: 'school-fire-movement-01-fc-2',
					role: 'watch_out',
					front: 'What can FIRE get wrong?',
					back: 'It can become extreme, joyless, or too spreadsheet driven.',
				},
				{
					id: 'school-fire-movement-01-fc-3',
					role: 'use_it',
					front: 'How do you use this lens?',
					back: 'Compare your current money habits against the future options they create.',
				},
			],
			applyAction: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
			revision: 1,
		},
		{
			id: 'school-fire-movement-02',
			title: 'Savings rate is the freedom lever',
			description:
				'This lesson teaches why FIRE people focus so much on savings rate instead of only income.',
			pillar: 'freedom-math',
			schoolsOfThought: ['fire-movement'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['savings-rate', 'spending-needs', 'timeline-pressure'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'savings rate power',
				'spending needs',
				'timeline pressure',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Most people think a higher income automatically means more freedom. FIRE disagrees. A high income helps, but if every raise becomes a bigger life, the freedom gap may not move much.',
				},
				{
					type: 'paragraph',
					text: 'FIRE pays attention to savings rate because it works from both sides. Saving more gives you more to build with, and needing less means the target for freedom can be smaller.',
				},
				{
					type: 'bullet_list',
					items: [
						'Higher income can help',
						'Lower spending needs can help',
						'The gap between income and spending matters most',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is chasing income while letting lifestyle inflation eat the whole raise.',
				},
				{
					type: 'example',
					text: 'If two people both get a $500 monthly raise, one spends it all and feels the same pressure. The other keeps part of it and slowly gives their future self more room to breathe.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that savings rate can sound simple when life is not simple. Rent, medical costs, family support, childcare, debt, and unstable work can make the gap harder to build.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens belongs in your Dashboard and Simulate. The point is not to copy someone else’s savings rate. The point is to see what your gap is doing for your freedom path.',
				},
				{
					type: 'key_takeaway',
					text: 'FIRE watches savings rate because the gap between income and spending becomes future choice.',
				},
			],
			audioScript:
				'FIRE people talk about savings rate so much that it can sound annoying. But there is a reason. Savings rate is one of the cleanest ways to see whether your money is creating future freedom or just passing through your hands.\n\nMost people focus on income first. That makes sense. More income can absolutely help. But FIRE adds a harder question: how much of that income actually stays in your life as future choice?\n\nImagine someone earns $60,000 and spends nearly all of it. Then they work hard, get promoted, and earn $80,000. Great. But if the apartment gets nicer, the car gets newer, subscriptions multiply, food delivery becomes normal, and every raise disappears into a bigger lifestyle, the pressure may not change. They earn more, but they still need every paycheck. Their life got more expensive at the same speed their income grew.\n\nFIRE looks at that and says the real lever is the gap. Income minus spending. That gap is where options come from.\n\nThe reason savings rate is powerful is that it works twice. First, saving more gives you more money to build with. Second, if you can live well on less, the amount needed to support your life can become smaller. That does not mean you should live a miserable life. It means your required spending matters. A person who needs $3,000 a month to feel stable has a different freedom target than someone who needs $7,000 a month.\n\nThis is where the FIRE lens can be both useful and dangerous. Useful, because it reveals the hidden cost of lifestyle inflation. Dangerous, because it can make people feel judged. Some people are not overspending because they are careless. Some are paying high rent, supporting family, managing debt, dealing with health costs, or trying to survive in an expensive area. FIRE should not pretend every situation is equal.\n\nThe mature version is not, save 70 percent or you failed. The mature version is, know your gap. Protect it when you can. Grow it when possible. Do not let every raise disappear before it changes your future.\n\nInside Nisk, you would use Dashboard to see your current picture, then Simulate to test changes. What happens if the gap grows by $100 a month? What about $300? What if spending drops a little, or income rises a little, or both? The projection is not a promise. It is a mirror. It shows whether the direction is helping your future self.\n\nSavings rate matters because it turns invisible drift into visible choice.',
			flashcards: [
				{
					id: 'school-fire-movement-02-fc-1',
					role: 'main_idea',
					front: 'Why savings rate?',
					back: 'It shows how much income becomes future freedom instead of current spending.',
				},
				{
					id: 'school-fire-movement-02-fc-2',
					role: 'watch_out',
					front: 'What eats raises?',
					back: 'Lifestyle inflation can absorb income before it changes your options.',
				},
				{
					id: 'school-fire-movement-02-fc-3',
					role: 'use_it',
					front: 'What should you compare?',
					back: 'Compare income, spending, and the gap between them.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate a higher savings rate',
				scenarioType: 'aggressive-savings',
			},
			revision: 1,
		},
		{
			id: 'school-fire-movement-03',
			title: 'The FI number is not the idol',
			description:
				'This lesson teaches how FIRE uses a freedom number as a thinking tool, not a perfect life answer.',
			pillar: 'freedom-math',
			schoolsOfThought: ['fire-movement'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['freedom-number', 'withdrawal-humility', 'number-worship'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'freedom number',
				'withdrawal humility',
				'number worship',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'FIRE gives people a number to aim at, and that can feel powerful. Finally, freedom is not vague. It has a shape. But the number can also become a trap if you start treating it like a magic finish line.',
				},
				{
					type: 'paragraph',
					text: 'FIRE uses a freedom number to estimate how much invested wealth might support a lifestyle. The point is not certainty. The point is direction, assumptions, and tradeoffs.',
				},
				{
					type: 'bullet_list',
					items: [
						'Spending affects the target',
						'Returns are uncertain',
						'Life changes can move the number',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking one number can answer every future question.',
				},
				{
					type: 'example',
					text: 'Someone may calculate that they need $1,000,000 for a simple lifestyle. That number can guide planning, but health changes, family needs, market returns, location, and work preferences can still change the path.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is number worship. A person can hit a target and still feel anxious, bored, lonely, or unsure what to do next. Money can create options, but it does not automatically create meaning.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use the number as a planning lens. Test assumptions, compare scenarios, and ask what kind of freedom you actually want.',
				},
				{
					type: 'key_takeaway',
					text: 'A freedom number is a compass, not a prophecy.',
				},
			],
			audioScript:
				'One reason FIRE spread is that it gives people a number. That matters. A lot of people feel trapped because freedom sounds vague. They know they do not want to work forever under pressure, but they do not know what the alternative would require. FIRE turns that vague feeling into a target.\n\nThat target is often called a FI number. It is the amount of invested assets someone estimates they may need to support their spending without depending completely on a paycheck. The number usually comes from spending needs and a withdrawal assumption. But the key word is assumption. Not promise. Not guarantee. Not magic.\n\nThe useful part is that the number forces honesty. If your lifestyle costs a lot, the target is bigger. If your lifestyle is leaner, the target can be smaller. If you want more cushion, the target changes. If you plan to keep earning some income, the target changes again. The number makes the tradeoffs visible.\n\nBut FIRE can get weird here. People can start worshiping the number. They refresh spreadsheets constantly. They compare themselves to strangers. They treat the target like a finish line where life will suddenly become perfect. That is not how real life works.\n\nYou can hit a number and still not know what you want your days to look like. You can have enough money on paper and still feel anxious because markets move. You can leave a job and realize the job was stressful, but it also gave structure, identity, and social contact. You can build wealth and still carry old fear around money.\n\nSo the mature FIRE lens says: use the number, but do not bow to it.\n\nThe number is a compass. It helps you ask better questions. How much does my current life cost? What would a calmer life cost? How much runway do I have? What changes if I work part time later? What if I move? What if I keep working but only choose work I actually respect? What if I need more margin because I support family?\n\nInside Nisk, the goal is to test assumptions. You are not trying to receive a perfect answer. You are trying to understand how decisions behave. A projection can show the relationship between spending, saving, investing, time, and risk. It cannot promise your future.\n\nThe FI number is useful because it makes freedom concrete. It becomes harmful when it replaces judgment. Use it to think. Do not use it to punish yourself.',
			flashcards: [
				{
					id: 'school-fire-movement-03-fc-1',
					role: 'main_idea',
					front: 'What is a freedom number?',
					back: 'A planning estimate for how much wealth may support your lifestyle.',
				},
				{
					id: 'school-fire-movement-03-fc-2',
					role: 'watch_out',
					front: 'What is number worship?',
					back: 'Treating one estimate like a perfect answer to life.',
				},
				{
					id: 'school-fire-movement-03-fc-3',
					role: 'use_it',
					front: 'How should you use it?',
					back: 'Test assumptions and compare scenarios without treating projections as promises.',
				},
			],
			applyAction: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
			revision: 1,
		},
		{
			id: 'school-fire-movement-04',
			title: 'FIRE changes how work feels',
			description:
				'This lesson teaches how the FIRE lens changes career decisions, burnout, bargaining power, and optionality.',
			pillar: 'freedom-math',
			schoolsOfThought: ['fire-movement'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['work-optionality', 'bargaining-power', 'burnout-buffer'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'work optionality',
				'career bargaining power',
				'burnout buffer',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Work feels different when you need it less. FIRE is not only about leaving work. It is also about changing the power balance between you and the paycheck.',
				},
				{
					type: 'paragraph',
					text: 'FIRE notices that money can become bargaining power. Runway, lower expenses, and growing assets can make it easier to leave bad work, negotiate, retrain, rest, move, or take a smarter risk.',
				},
				{
					type: 'bullet_list',
					items: [
						'Runway makes quitting less desperate',
						'Lower expenses reduce pressure',
						'Assets can create patience',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is waiting until burnout becomes an emergency before building options.',
				},
				{
					type: 'example',
					text: 'Someone with six months of runway may handle a layoff differently than someone with two weeks of cash. The same event can feel like panic or a difficult transition.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that chasing FIRE too aggressively can make work feel like an enemy. That can be unhealthy if work also gives purpose, skill, friendship, and growth.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens connects to runway and scenario testing. You are asking how much breathing room your current money system gives you when work changes.',
				},
				{
					type: 'key_takeaway',
					text: 'FIRE is not just about quitting work. It is about reducing the fear behind work.',
				},
			],
			audioScript:
				'FIRE changes how you look at work. Not because work is bad. Work can be meaningful. Work can build skill, confidence, friendships, identity, and pride. But work feels very different when you have no choice.\n\nIf one missed paycheck would wreck your life, your job has more power over you. If your expenses are high, your choices narrow. If you have no runway, a bad manager, a layoff, a health issue, or a family emergency can turn into a full panic. FIRE pays attention to that pressure.\n\nThe point is not always, retire as young as possible. For many people, the more useful goal is optionality. Optionality means you have room to move. You can say no. You can take time to search for the right role instead of grabbing the first one. You can leave a toxic job before it destroys your health. You can retrain. You can move cities. You can start something slowly. You can care for family without every week feeling like financial danger.\n\nThat is why runway matters. That is why lower required spending matters. That is why assets matter. They do not just sit on a screen. They change your emotional relationship with work.\n\nImagine two people lose a job. One has no savings, high fixed bills, and a car payment that eats their margin. The other has lower expenses and several months of runway. Same event, very different experience. The first person may need immediate income at almost any cost. The second person still has stress, but they also have time. Time is power.\n\nBut FIRE has a blind spot here too. If you turn work into the enemy, you might miss the parts of work that are actually valuable. Some people do not want to retire early. They want better work. Calmer work. More flexible work. More meaningful work. Work with boundaries. Work they choose instead of work they cling to in fear.\n\nThat is a more grounded version of FIRE. It does not ask, how fast can I disappear from society? It asks, how can money reduce the fear behind my decisions?\n\nInside Nisk, you would test this through your runway and scenarios. How many months could you breathe if income stopped? What changes if you lower fixed costs? What changes if you save more each month? What changes if you build a plan around career flexibility instead of only retirement?\n\nFIRE is powerful because it makes work less like a cage and more like a choice.',
			flashcards: [
				{
					id: 'school-fire-movement-04-fc-1',
					role: 'main_idea',
					front: 'How does FIRE change work?',
					back: 'It lowers paycheck dependence and increases bargaining power.',
				},
				{
					id: 'school-fire-movement-04-fc-2',
					role: 'watch_out',
					front: 'What is the work trap?',
					back: 'Treating all work as bad can ignore purpose, growth, and connection.',
				},
				{
					id: 'school-fire-movement-04-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Test how much runway and lower expenses change your work options.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-fire-movement-05',
			title: 'Where FIRE breaks down',
			description:
				'This lesson teaches the limits of FIRE and how the lens can become too extreme, privileged, or disconnected from real life.',
			pillar: 'freedom-math',
			schoolsOfThought: ['fire-movement'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'fire-blind-spots',
				'life-flexibility',
				'sustainable-freedom',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'fire blind spots',
				'life flexibility',
				'sustainable freedom',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'FIRE is useful, but it can go wrong. Any lens that explains a lot can start pretending it explains everything.',
				},
				{
					type: 'paragraph',
					text: 'FIRE breaks down when it ignores real constraints. Not everyone has the same income, costs, health, family support, safety, location, or starting point. A plan that feels disciplined for one person may be unrealistic for another.',
				},
				{
					type: 'bullet_list',
					items: [
						'Income is not equal',
						'Fixed costs can be heavy',
						'Family obligations change the math',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is turning FIRE into a purity test instead of a decision lens.',
				},
				{
					type: 'example',
					text: 'A single person with low rent and strong income may save aggressively without much pain. A parent supporting relatives in an expensive city may need a very different path.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that FIRE can overvalue future freedom and undervalue the present. A life that sacrifices every good thing now may not feel free, even if the spreadsheet improves.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, the mature move is to test the lens without copying it blindly. Use FIRE to ask sharper questions, then adapt the answers to your real numbers and real life.',
				},
				{
					type: 'key_takeaway',
					text: 'FIRE is a strong lens when it creates options, and a weak one when it becomes a purity contest.',
				},
			],
			audioScript:
				'Every school of thought has a point where it starts to break down. FIRE is no different. The idea is powerful because it makes time, spending, investing, and work feel connected. But if you take it too far, it can become rigid.\n\nThe first problem is that FIRE can sound more universal than it really is. Some people have high income, low rent, no dependents, good health, and a stable job. Saving aggressively may be difficult, but possible. Other people are supporting parents, raising kids, handling medical costs, living in expensive cities, recovering from debt, or dealing with unstable hours. Telling both people to follow the same path is not wisdom. It is lazy.\n\nThe second problem is that FIRE communities can sometimes turn into comparison machines. People compare savings rates, net worth milestones, ages, portfolios, spending levels, and retirement dates. That can motivate some people, but it can also create shame. Nisk should not use FIRE that way. Your financial path is not a race against strangers with different incomes, families, health, luck, and starting lines.\n\nThe third problem is present life. Future freedom matters, but today is still part of your life. If the plan makes you isolated, bitter, unhealthy, or scared to spend on anything meaningful, something is off. FIRE should protect your life, not shrink it into a math contest.\n\nAnd then there is uncertainty. Markets move. Housing costs change. Health changes. Family needs change. Your own desires change. Maybe you think you want to stop working forever, then later realize you want work with better boundaries. Maybe you think you want to live in one place, then your family situation changes. A FIRE plan has to stay flexible because real life does not care about your spreadsheet.\n\nThat does not mean FIRE is wrong. It means FIRE is a lens, not a religion. It is at its best when it asks better questions. How much of my income becomes future freedom? What spending actually improves my life? What am I buying with my time? How much runway would make me less afraid? What kind of work would I choose if I had more options?\n\nInside Nisk, the mature move is to test the idea on your own numbers. Not copy someone else. Not shame yourself. Not assume early retirement is the only valid goal. Use the lens to understand the tradeoffs, then build a version that respects your actual life.\n\nFIRE breaks down when it becomes purity. It works when it gives you more options.',
			flashcards: [
				{
					id: 'school-fire-movement-05-fc-1',
					role: 'main_idea',
					front: 'Where can FIRE fail?',
					back: 'It can ignore real constraints and turn freedom into a purity contest.',
				},
				{
					id: 'school-fire-movement-05-fc-2',
					role: 'watch_out',
					front: 'What should FIRE avoid?',
					back: 'Shame, comparison, extreme sacrifice, and pretending every starting point is equal.',
				},
				{
					id: 'school-fire-movement-05-fc-3',
					role: 'use_it',
					front: 'How do you adapt FIRE?',
					back: 'Test the lens on your own numbers and real life constraints.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Write your FIRE boundaries',
				prompt: 'Write three things you are not willing to sacrifice for a faster freedom timeline.',
			},
			revision: 1,
		},
	],
};
